import { api } from './client';

async function readApiErrorMessage(response: Response, fallback: string) {
  try {
    const json = await response.clone().json();
    return String(json?.message || '').trim() || fallback;
  } catch (_error) {
    return fallback;
  }
}

export type QuickTaskSource = {
  type?: string;
  id?: string | null;
  messageId?: string | null;
  threadId?: string | null;
  scope?: string | null;
  title?: string | null;
  excerpt?: string | null;
  messageText?: string | null;
  eventUuid?: string | null;
  artistId?: string | null;
};

export type QuickTaskCreateRequest = {
  title: string;
  description?: string | null;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  dueOn?: string | null;
  assigneeIds?: number[];
  artistAssigneeIds?: string[];
  source?: QuickTaskSource;
  sourceType?: string;
  eventUuid?: string | null;
  artistId?: string | null;
};

export async function createQuickTask(body: QuickTaskCreateRequest) {
  const { data, response } = await (api as any).POST('/admin/quick-tasks', { body });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'クイックタスクの作成に失敗しました。'));
  }
  return data || {};
}

export async function listQuickTasks(query: Record<string, unknown> = {}) {
  const { data, response } = await (api as any).GET('/admin/quick-tasks', {
    params: { query },
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'クイックタスクの取得に失敗しました。'));
  }
  return data || { items: [] };
}

export async function completeQuickTask(taskId: string) {
  const { data, response } = await (api as any).PATCH('/admin/quick-tasks/{taskId}/complete', {
    params: { path: { taskId } },
    body: {},
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'クイックタスクの完了に失敗しました。'));
  }
  return data || {};
}

export async function remindQuickTask(taskId: string, body: { messageKey: string }) {
  const { data, response } = await (api as any).POST('/admin/quick-tasks/{taskId}/remind', {
    params: { path: { taskId } },
    body,
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'リマインドの送信に失敗しました。'));
  }
  return data || {};
}

export async function searchQuickTaskAdminAssignees(query = '') {
  const { data, response } = await (api as any).GET('/admin/quick-tasks/assignee-candidates', {
    params: { query: { q: query, limit: 30 } },
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, '管理者候補の取得に失敗しました。'));
  }
  return Array.isArray(data?.admins) ? data.admins : [];
}

export async function searchQuickTaskArtistAssignees(query = '') {
  const { data, response } = await (api as any).GET('/admin/artists', {
    params: {
      query: {
        name: query,
        page: 1,
        limit: 30,
        sort: query ? 'created_desc' : 'login',
      },
    },
  });
  if (!response.ok) {
    throw new Error(await readApiErrorMessage(response, 'アーティスト候補の取得に失敗しました。'));
  }
  return Array.isArray(data?.artists) ? data.artists : [];
}
